import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { API_BASE } from '../apiBase';
import { useDropzone } from 'react-dropzone';

const ImageCompressor: React.FC = () => {
    const [file, setFile] = useState<File | null>(null);
    const [result, setResult] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const onDrop = useCallback((acceptedFiles: File[]) => {
        setFile(acceptedFiles[0]);
        setResult(null);
        setError(null);
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'image/*': ['.jpeg', '.jpg', '.png', '.webp']
        },
        multiple: false
    });

    const handleCompress = async () => {
        if (!file) return;

        setLoading(true);
        setError(null);

        const formData = new FormData();
        formData.append('image', file);

        try {
            const response = await axios.post(`${API_BASE}/api/compress-image`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data.success) {
                setResult(response.data);
            } else {
                setError(response.data.error || 'Compression failed');
            }
        } catch (err: any) {
            console.error('Compression error:', err);
            setError(err.response?.data?.error || err.message || 'An error occurred during compression');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto p-6 space-y-8">
            <div className="text-center space-y-4">
                <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                    AI Image Compressor
                </h2>
                <p className="text-slate-600 max-w-2xl mx-auto">
                    Smart compression that retains quality while reducing file size. Perfect for web and social media.
                </p>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-100">
                <div
                    {...getRootProps()}
                    className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all duration-300
                        ${isDragActive ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-blue-400 hover:bg-slate-50'}
                        ${file ? 'bg-slate-50 border-blue-400' : ''}
                    `}
                >
                    <input {...getInputProps()} />
                    {file ? (
                        <div className="space-y-4">
                            <div className="text-6xl">📄</div>
                            <div>
                                <p className="text-xl font-semibold text-slate-800">{file.name}</p>
                                <p className="text-sm text-slate-500">{(file.size / 1024).toFixed(1)} KB</p>
                            </div>
                            <p className="text-blue-600 text-sm font-medium">Click or drag to replace</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="text-6xl text-slate-300">☁️</div>
                            <div>
                                <p className="text-xl font-semibold text-slate-700">Drop your image here</p>
                                <p className="text-slate-500 mt-1">or click to browse files</p>
                            </div>
                            <p className="text-xs text-slate-400">Supports JPG, PNG, WEBP</p>
                        </div>
                    )}
                </div>

                <div className="mt-8 flex justify-center">
                    <button
                        onClick={handleCompress}
                        disabled={!file || loading}
                        className={`
                            px-8 py-3 rounded-full font-bold text-lg shadow-lg transition-all transform hover:-translate-y-0.5
                            ${!file || loading
                                ? 'bg-slate-200 text-slate-400 cursor-not-allowed shadow-none'
                                : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:shadow-blue-200'}
                        `}
                    >
                        {loading ? (
                            <span className="flex items-center gap-2">
                                <span className="animate-spin">⏳</span> Compressing...
                            </span>
                        ) : 'Compress Image ✨'}
                    </button>
                </div>

                {error && (
                    <div className="mt-6 p-4 bg-red-50 text-red-600 rounded-lg text-center border border-red-100">
                        {error}
                    </div>
                )}
            </div>

            {result && (
                <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-100 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <h3 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-2">
                        <span>🎉</span> Compression Complete!
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="p-4 rounded-xl bg-slate-50 border border-slate-100 text-center">
                            <p className="text-sm text-slate-500 mb-1">Original Size</p>
                            <p className="text-2xl font-bold text-slate-800">{result.originalSizeKB} KB</p>
                        </div>
                        <div className="p-4 rounded-xl bg-green-50 border border-green-100 text-center">
                            <p className="text-sm text-green-600 mb-1">New Size</p>
                            <p className="text-2xl font-bold text-green-700">{result.compressedSizeKB} KB</p>
                        </div>
                        <div className="p-4 rounded-xl bg-blue-50 border border-blue-100 text-center">
                            <p className="text-sm text-blue-600 mb-1">Saved</p>
                            <p className="text-2xl font-bold text-blue-700">{result.compressionRatio}</p>
                        </div>
                    </div>

                    <div className="mt-8 text-center">
                        <a
                            href={`${API_BASE}${result.downloadUrl}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-8 py-3 bg-slate-900 text-white rounded-lg font-semibold hover:bg-slate-800 transition-colors"
                        >
                            <span>⬇️</span> Download Compressed Image
                        </a>
                        <p className="mt-2 text-xs text-slate-400">Opens in new tab</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ImageCompressor;
